export const resumeData = {
  name: 'Matt Gordon',
  pronouns: 'he/him',
  title: 'Senior Frontend Software Engineer',

  profile: [
    'Agile-minded engineer focused on delivering web applications with clean design, efficient code, and accessibility at the forefront.',
    'Strong front-end expertise with modern frameworks and responsive UI design.',
    'Hands-on experience building scalable APIs and integrations with Node.js and Express.',
    'Passionate about clean code, user experience, and domain-driven design.',
    'Collaborative team member and mentor who leads with empathy, quality, and clear communication.'
  ],

  contact: {
    email: 'jobs@mattrgordon.com',
    linkedin: 'https://www.linkedin.com/in/mgordon82',
    github: 'https://github.com/mgordon82',
    location: 'Minnesota, USA'
  },

  location: 'Minnesota, USA',
  email: 'jobs@mattrgordon.com',
  links: [
    { label: 'LinkedIn', url: 'https://www.linkedin.com/in/mgordon82' },
    { label: 'GitHub', url: 'https://github.com/mgordon82' }
  ],

  experience: [
    {
      role: 'Senior Front-End / Full Stack Engineer',
      company: 'Sempris / Slyce360',
      location: 'Eden Prairie, MN',
      start: 'April 2024',
      end: 'November 2025',
      bullets: [
        'Architected and delivered modern React-based dashboard applications serving hundreds of weekly users across enterprise marketing teams',
        'Rebuilt multiple legacy platforms into scalable React applications, significantly improving maintainability and development velocity',
        'Led front-end state architecture using Redux Toolkit (Slices & Epics), ensuring consistent data flow across complex UI workflows',
        'Designed and implemented REST APIs in Node.js (Next.js API routes) connected to MySQL and MongoDB backends',
        'Integrated third-party services including Mandrill and Twilio to automate marketing communications and reduce manual campaign management',
        'Contributed within a 7-engineer team, collaborating closely on architecture decisions, code reviews, and feature planning',
        'Established front-end coding standards and review practices to improve quality and reduce regression issues'
      ],
      tech: [
        'ReactJS',
        'Redux Toolkit',
        'Redux-Observable',
        'NextJS',
        'Node.js',
        'MySQL',
        'MongoDB',
        'Mandrill',
        'Twilio'
      ]
    },
    {
      role: 'Senior Web Developer - UI/UX',
      company: 'eLumen, Inc.',
      location: 'Minneapolis, MN',
      start: 'March 2022',
      end: 'November 2023',
      bullets: [
        'Led UI/UX development within a 15-person cross-functional team (10 engineers), delivering a production React SaaS platform for higher education institutions',
        'Built and deployed React applications to AWS environments, contributing to CI/CD workflows and Jenkins deployments',
        'Improved accessibility compliance (WCAG 2.1) through structured testing and remediation using axe and Wave tools',
        'Designed reusable component systems to increase development efficiency and consistency across features',
        'Mentored junior developers in modern React patterns, hooks, and front-end architecture principles',
        'Partnered directly with customers to translate user feedback into product enhancements and UI refinements'
      ],
      tech: [
        'ReactJS',
        'AWS',
        'Jenkins',
        'Figma',
        'Sketch',
        'PyTests',
        'Jest',
        'Agile'
      ]
    },
    {
      role: 'Senior Front-End Developer',
      company: 'Flint Hills Resources',
      location: 'Inver Grove Heights, MN',
      start: 'October 2020',
      end: 'March 2022',
      bullets: [
        'Collaborated with 4 nationwide teams to lead architecture improvements, resulting in 20% increased maintainability of forecasting dashboards.',

        'Enhanced UI efficiency by 25% by developing React apps integrating C3.ai to visualize operational and forecasting data.',

        'Streamlined UI consistency across 5 nationwide teams by standardizing 10+ component patterns, reducing duplication by 30%.',

        'Improved forecasting dashboard maintainability by 40% by leading architectural discussions and migrating legacy components to React Hooks.'
      ],
      tech: ['ReactJS', 'C3.ai', 'Figma', 'Sketch']
    },
    {
      role: 'Lead UI Designer / Developer',
      company: 'Waterfield Technologies',
      location: 'Minneapolis, MN',
      start: 'February 2020',
      end: ' October 2020',
      bullets: [
        'Built and deployed React-based IVR administration tools for enterprise clients',

        'Redesigned chat and IVR management interfaces to improve usability and workflow efficiency',

        'Contributed to AWS deployments and collaborated across distributed product teams'
      ],
      tech: ['ReactJS', 'AWS', 'UI/UX']
    },
    {
      role: 'Software Engineer - UI/UX Lead',
      company: 'UnitedHealth Group / Optum',
      location: 'Boston, MA & Eden Prairie, MN',
      start: 'October 2014',
      end: 'February 2020',
      bullets: [
        ' Delivered provider-facing web portals supporting hundreds of healthcare providers across national networks, ensuring reliability and compliance within enterprise healthcare systems.',

        'Delivered UI systems using React, AngularJS, HTML, CSS, and JavaScript in enterprise environments.',

        'Collaborated within a 7-engineer team to deliver secure, reliable healthcare applications.',

        'Facilitated usability sessions and incorporated provider feedback into production releases.',

        'Mentored developers and contributed to sprint planning, estimation, and cross-team coordination.'
      ],
      tech: [
        'HTML',
        'CSS',
        'JavaScript',
        'ReactJS',
        'AngularJS',
        'Adobe',
        'Agile'
      ]
    },
    {
      role: 'Online User Interface Manager',
      company: 'The Conference Exchange',
      location: 'Cumberland, RI',
      start: 'August 2011',
      end: 'August 2014',
      bullets: [
        'Designed and redesigned application pages for in-house applications.',
        'Built pages intended for use with clients during conferences.',
        'Involved in the setup and testing of audio and video equipment at conferences.',
        'Attended conferences around the country.',
        'Built a conference information kiosk in HTML, CSS, and JavaScript.'
      ],
      tech: ['HTML', 'CSS', 'JavaScript']
    },
    {
      role: 'Miscellaneous Projects / Freelance',
      company: 'Online Presence',
      location: 'Remote',
      start: 'January 2003',
      end: 'Present',
      bullets: [
        'Started as a hobby, worked on side projects to help small businesses establish online presence.',
        'Mostly involved in low to moderate effort projects using WordPress or Drupal.',
        'Worked on building basic ReactJS applications.',
        'Deployed ReactJS applications to a static server through Git.',
        'HTML, CSS, and JavaScript work.'
      ],
      tech: ['WordPress', 'Drupal', 'ReactJS', 'HTML', 'CSS', 'JavaScript']
    }
  ],

  education: [
    {
      school: 'Wentworth Institute of Technology',
      location: 'Boston, MA',
      credential: 'Associate in Electronic Engineering Technology',
      year: '2003'
    },
    {
      school: 'Zero to Mastery',
      credential: 'NodeJS Course Certificate',
      year: '2025'
    },
    {
      school: 'Zero to Mastery',
      credential: 'Complete Web Developer Certificate',
      year: '2026'
    }
  ],

  skills: {
    highlights: [
      'ReactJS applications within AWS cloud, using CI/CD pipelines for deployment.',
      'Coordinating and building reusable components for multiple projects.',
      'Provide collaborative and creative ideas and solution options for projects.',
      'Mentor and guide junior developers.'
    ],
    technology: [
      'Mac preferred',
      'Agile',
      'CSS',
      'HTML',
      'ReactJS',
      'Node.js',
      'VSCode / VSCodium',
      'AWS',
      'Figma',
      'Sketch',
      'Adobe',
      'Git & GitHub',
      'aXe Chrome extension',
      'WAVE accessibility tool'
    ]
  },

  projects: [
    {
      name: 'MiPT',
      description:
        'Full-stack fitness and nutrition platform for trainers and clients, featuring secure role-based access, progress tracking, nutrition tools, and AI-powered insights.',
      link: 'https://mipt-ui.netlify.app/',
      code: 'https://github.com/mgordon82/chapter-two-api',
      tech: [
        'React',
        'TypeScript',
        'Redux Toolkit',
        'Redux Observable',
        'MUI',
        'Node.js',
        'Express',
        'MongoDB',
        'AWS Cognito',
        'OpenAI'
      ]
    },
    {
      name: 'Professional Portfolio',
      description:
        'Dark-mode, responsive portfolio site built with React and MUI, featuring interactive project cards and a custom MG logo.',

      code: 'https://github.com/mgordon82/professional-portfolio',
      tech: ['React', 'MUI', 'Netlify']
    },
    {
      name: 'Doggo-e-Doggo',
      description:
        'React app that connects to a paginated dog adoption API, supports filters, and showcases available dogs for adoption.',
      link: 'https://doggo-e-doggo.netlify.app',
      code: 'https://github.com/mgordon82/doggo-e-doggo',
      tech: ['React', 'REST API', 'Netlify']
    }
  ]
};
